import React from "react";
import { DashboardDocument } from "components/resources/dashboards/DashboardDocument";
import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import { Alert, Box, Button, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Select, SelectChangeEvent, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SimpleFormLabel, SimpleFormTextField } from "components/dialogs/SimpleFormDialog";
import APIRequest, { APIError } from "system/APIRequest";
import { useNavigate } from "react-router-dom";
import { SelectedTemplateQuery } from "routes/dashboard/DashboardPageRoot";

export interface ConvertDashboardToReportDialogProps {
  dashboard: DashboardDocument;
  templateQueries: Array<SelectedTemplateQuery>;
  selectedTemplateQueryIndex: number;

  onClose: () => void;
}


export default function ConvertDashboardToReportDialog(props: ConvertDashboardToReportDialogProps): JSX.Element {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const { dashboard, templateQueries, selectedTemplateQueryIndex } = props;

  const [newReportTitle, setNewReportTitle] = React.useState<string>(dashboard.name);
  const [activeTemplateQueryIndex, setActiveTemplateQueryIndex] = React.useState<number>(selectedTemplateQueryIndex);
  const [apiError, setApiError] = React.useState<APIError | undefined>(undefined);
  const [newReportId, setNewReportId] = React.useState<string | undefined>(undefined);

  const activeTemplateQuery = React.useMemo(() => {
    if (templateQueries && templateQueries.length > 0) {
      return templateQueries[activeTemplateQueryIndex];
    }
  }, [templateQueries, activeTemplateQueryIndex]);


  const createReport = () => {
    const dashboardId = dashboard.id;
    const templateQueryId = activeTemplateQuery?.id;

    setNewReportId(undefined);

    if (!dashboardId || !templateQueryId) {
      return;
    }

    APIRequest(`obj/dashboard/${dashboardId}/duplicate`, {}, {method: "POST"}).then((response) => {
      if (!response?.id) {
        return;
      }
      APIRequest(`obj/dashboard/${response.id}`, {}, {
        method: "PUT",
        body: JSON.stringify({
          name: newReportTitle,
          template_type: null,
          data: {
            ...(dashboard.data || {}),
          defaultDashboardQuery: templateQueryId
        }})
      }).then(() => {
        setNewReportId(response.id);
      }).catch((e) => {
        setApiError(e);
      });
    }).catch((e) => {
      setApiError(e);
    });
  }

  return (
    <ResponsiveDialog
      maxWidth="sm"
      open={true}
      onClose={() => props.onClose()}>
       <DialogTitle>
          <Typography variant="h4">
            {i18n.t("dashboard.convert-template-to-report-dialog.dialog-title")}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: theme.palette.background.default, p: 1 }} >
          <Paper sx={{p: 2}}>
          {!newReportId &&
            <>
            <Alert variant="outlined" severity="info" sx={{ mb: 2 }}>
              {i18n.t("dashboard.convert-template-to-report-dialog.description")}
            </Alert>
            <Box>
              <SimpleFormLabel>{i18n.t("dashboard.convert-template-to-report-dialog.name-field")}</SimpleFormLabel>
              <SimpleFormTextField value={newReportTitle} onChange={(e) => setNewReportTitle(e.target.value)} />
            </Box>
            {templateQueries.length > 1 &&
              <Box>
                <SimpleFormLabel>{i18n.t("dashboard.convert-template-to-report-dialog.select-active-query")}</SimpleFormLabel>
                <Select
                  label={i18n.t("dashboard.convert-template-to-report-dialog.select-active-query")}
                  fullWidth
                  onChange={(e: SelectChangeEvent) => {
                    setActiveTemplateQueryIndex(parseInt(e.target.value));
                  }}
                  value={activeTemplateQueryIndex + ""}>
                  {templateQueries.map((query, index) => (
                    <MenuItem key={index + ""} value={index + ""}>
                      {query.query.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            }
            </>
          }
          {newReportId &&
            <Alert variant="outlined" severity="success" sx={{ mb: 2 }}>
              {i18n.t("dashboard.convert-template-to-report-dialog.success")}
            </Alert>
          }
          {apiError &&
            <Alert severity="error">{apiError.server_message || String(apiError)}</Alert>
          }
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={props.onClose}
            variant="contained"
            color="secondary" >
            {i18n.t("cancel")}
          </Button>
          {newReportId &&
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                navigate(`/dashboard/${newReportId}`);
                setNewReportId(undefined);
                props.onClose();
              }} >
              {i18n.t("dashboard.convert-template-to-report-dialog.open-report")}
            </Button>
          }
          {!newReportId &&
            <Button
              variant="contained"
              color="primary"
              onClick={createReport} >
              {i18n.t("dashboard.convert-template-to-report-dialog.create-report")}
            </Button>
          }
        </DialogActions>
    </ResponsiveDialog>
  );
}